import {Module} from 'virtual-tour-viewer/src/modules/React360Proxy';
import {getEnvironmentName} from 'virtual-tour-viewer/src/utils/UIHelper';
import EdapEventNames from './EdapEventNames';
import packageJson from '../../package.json';

let _cookieHasQueryparamName = 'cookie_has';
let _cookieHavQueryparamName = 'cookie_hav';
let _cookieHasQueryparamValue;
let _cookieHavQueryparamValue;

let _localeQueryparamName = 'locale';
let _localeQueryparamValue;

let _originSiteIdQueryparamName = 'site_id';
let _originSiteIdQueryparamValue = -1;

function setEDAPCookies() {
    let urlVars = new URL(window.location.href).searchParams;
    if (urlVars.has(_cookieHasQueryparamName)) {
        _cookieHasQueryparamValue = urlVars.get(_cookieHasQueryparamName);
    }

    if (urlVars.has(_cookieHavQueryparamName)) {
        _cookieHavQueryparamValue = urlVars.get(_cookieHavQueryparamName);
    }

    if (urlVars.has(_localeQueryparamName)) {
        _localeQueryparamValue = urlVars.get(_localeQueryparamName);
    }

    if (urlVars.has(_originSiteIdQueryparamName)) {
        _originSiteIdQueryparamValue = parseInt(urlVars.get(_originSiteIdQueryparamName), 10) || -1;
    }
}

function configureAnalyticsDatalayer() {
    if (window.edap) {
        // Throwing error causes application to crash.
        // eslint-disable-next-line no-console
        console.error('Attempted to configure Analytics Datalayer after edap was loaded.');
    }
    setEDAPCookies();
    let glob = window;
    // Initialize ADL
    // Refer: https://github.homeawaycorp.com/pages/edap/adl-json-schema/specs/9_10_0/global
    glob.analyticsdatalayer = {
        appenvironment: getEnvironmentName(),
        appname: 'vrtourviewer',
        appversion: packageJson.version,
        displaylocale: _localeQueryparamValue,
        monikerbrand: 'homeaway_us',
        mpaasregion: '-1',
        pageflow: '-1',
        pagename: 'vrViewer',
        pagetype: 'virtual tours',
        publicuuid: '-1',
        sensitive: 'false',
        visitortype: 'traveler',
        sessionid: _cookieHasQueryparamValue,
        visitorid: _cookieHavQueryparamValue,
    };
}

configureAnalyticsDatalayer();

class EdapIntegrationProxy extends Module {
    constructor() {
        super('EdapIntegrationProxy');
    }

    _triggerEvent(eventName, eventData) {
        let edapInstance = require('@homeaway/edap-integrations');
        edapInstance.trigger(eventName, {
            ...eventData,
            ...{viewerversion: 'v2'},
            ...{origin_site_id: _originSiteIdQueryparamValue},
        });
    }

    emitSphereVisit(sphereId, floorId, tourId) {
        let edapData = {
            floorid: floorId,
            sphereId,
            tourid: tourId,
        };
        this._triggerEvent(EdapEventNames.SphereVisit, edapData);
    }

    emitSpherePresented(sphereId, tourId, loadTime, initialImageQuality, imageFormat, isInitial) {
        let edapData = {
            sphere_id: sphereId,
            tour_id: tourId,
            load_time: loadTime,
            initial_image_quality: initialImageQuality,
            image_format: imageFormat,
            is_initial: isInitial,
        };
        this._triggerEvent(EdapEventNames.SpherePresented, edapData);
    }

    emitNavMenuPresented(tourId, menuTypePresented) {
        let edapData = {
            tourid: tourId,
            menu_type: menuTypePresented,
        };
        this._triggerEvent(EdapEventNames.NavMenuPresented, edapData);
    }

    emitNavMenuFormToggled(tourId, toggleMethod) {
        let edapData = {
            tourid: tourId,
            toggle_method: toggleMethod,
        };
        this._triggerEvent(EdapEventNames.NavMenuFormToggled, edapData);
    }

    emitNavMenuGroupSelected(tourId, groupId, groupLabel) {
        let edapData = {
            tourId,
            floorId: groupId,
            group_name: groupLabel,
        };
        this._triggerEvent(EdapEventNames.NavMenuGroupSelected, edapData);
    }

    emitNavMenuGroupPresented(tourId, groupId, groupLabel, spaceLabels) {
        let edapData = {
            tourId,
            floorId: groupId,
            group_name: groupLabel,
            num_spaces: spaceLabels.length,
            space_list: spaceLabels,
        };
        this._triggerEvent(EdapEventNames.NavMenuGroupPresented, edapData);
    }

    emitTourNavigation(
        method,
        originSphereId,
        destinationSphereId,
        tourId,
        navigationOrder,
        originSpaceLabel?,
        originSpaceId?,
        originGroupId?,
        destinationSpaceLabel?,
        destinationSpaceId?,
        destinationGroupId?
    ) {
        let edapData = {
            navigation_method: method,
            origin_sphere_id: originSphereId,
            destination_sphere_id: destinationSphereId,
            tourid: tourId,
            tour_navigation_order: navigationOrder,
            origin_space_label: originSpaceLabel,
            origin_space_id: originSpaceId,
            origin_group_id: originGroupId,
            destination_space_label: destinationSpaceLabel,
            destination_space_id: destinationSpaceId,
            destination_group_id: destinationGroupId,
        };
        this._triggerEvent(EdapEventNames.SphereNavigation, edapData);
    }

    emitFloorStart(floorId, tourId) {
        let edapData = {
            floorid: floorId,
            tourid: tourId,
        };
        this._triggerEvent(EdapEventNames.FloorStart, edapData);
    }

    emitHotSpotDisplayed(sphereId, tourId) {
        let edapData = {
            sphere_id: sphereId,
            tour_id: tourId,
        };
        this._triggerEvent(EdapEventNames.HotSpotDisplayed, edapData);
    }

    emitSphereInteraction(tourId, floorId, sphereId, interactionType) {
        let edapData = {
            floorid: floorId,
            tourid: tourId,
            sphereid: sphereId,
            virtual_tour_sphere_interaction_type: interactionType,
        };
        this._triggerEvent(EdapEventNames.SphereInteracted, edapData);
    }

    emitTutorialPresented(tourId, stepIndex, automated, transitionSource) {
        let edapData = {
            tourid: tourId,
            automated_transition: automated,
            step_index: stepIndex,
            transition_source: transitionSource,
        };
        this._triggerEvent(EdapEventNames.TutorialPresented, edapData);
    }

    emitTutorialHidden(tourId, stepIndex, automated, transitionSource) {
        let edapData = {
            tourid: tourId,
            automated_transition: automated,
            step_index: stepIndex,
            transition_source: transitionSource,
        };

        this._triggerEvent(EdapEventNames.TutorialHidden, edapData);
    }
}

export default new EdapIntegrationProxy();
